import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ErrorBoundary, captureException, initErrorReporter } from '@vastra/ui';
import App from './App';
import './styles/global.css';

initErrorReporter({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_SENTRY_ENVIRONMENT ?? process.env.NODE_ENV,
  release: process.env.REACT_APP_SENTRY_RELEASE,
});

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    captureException(event.error ?? event.message, {
      app: 'customer-app',
      tags: { source: 'window.onerror' },
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    captureException(event.reason, {
      app: 'customer-app',
      tags: { source: 'unhandledrejection' },
    });
  });
}

/**
 * Register the storefront service worker in production only.
 *
 * In dev we rely on webpack-dev-server HMR and don't want a SW caching stale
 * chunks. An opt-out via `REACT_APP_DISABLE_SW=1` is kept as an emergency
 * kill switch if a bad SW ever ships; the body here also unregisters any
 * previously-installed SW so the kill switch actually kills.
 */
if (
  typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && process.env.NODE_ENV === 'production'
) {
  if (process.env.REACT_APP_DISABLE_SW === '1') {
    void navigator.serviceWorker
      .getRegistrations()
      .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      .catch(() => undefined);
  } else {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/service-worker.js')
        .catch((err) => {
          captureException(err, { app: 'customer-app', tags: { source: 'sw-register' } });
        });
    });
  }
}

const container = document.getElementById('root');
if (!container) throw new Error('Root element not found');

const root = createRoot(container);
root.render(
  <React.StrictMode>
    <ErrorBoundary app="customer-app">
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);
